import React from 'react'
import type { ICommonLinkProps } from '@frontend/pole-ui/lib/components/CommonLink'
import { ClockIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ClockIcon'
import { PaperIcon } from '@frontend/pole-ui/lib/components/Icon/svg/PaperIcon'
import { PaperThreeLinesIcon } from '@frontend/pole-ui/lib/components/Icon/svg/PaperThreeLinesIcon'
import { ShieldDoneIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShieldDoneIcon'
import { StarIcon } from '@frontend/pole-ui/lib/components/Icon/svg/StarIcon'
import { WalletOnePocketIcon } from '@frontend/pole-ui/lib/components/Icon/svg/WalletOnePocketIcon'
import { WalletTwoPocketsIcon } from '@frontend/pole-ui/lib/components/Icon/svg/WalletTwoPocketsIcon'
import { WheatIcon } from '@frontend/pole-ui/lib/components/Icon/svg/WheatIcon'
import type { TLandingDetail } from '@/components/composed/landingBlocks/LandingDetails/types'
import { getLinkFormatOption } from '@/components/composed/landingBlocks/LandingFAQ/helpers'
import type { TLandingFAQItem } from '@/components/composed/landingBlocks/LandingFAQ/types'
import type { TLandingStep } from '@/components/composed/landingBlocks/LandingSteps/types'
import type { IBreadcrumb } from '@/components/ui/Breadcrumbs/Breadcrumb/interfaces'
import { EFinancingOfferRoute } from '@/types'
import type { TFormatOptions } from '@/utils/formatUtils'
import type { TLandingBenefit } from './components/LandingBenefits/types'
import type { TLandingLoanTermGroup } from './components/LandingLoanTerms/types'
import { messages } from './messages'

export const LANDING_DETAILS: Readonly<TLandingDetail[]> = [
  {
    icon: <WalletTwoPocketsIcon />,
    value: messages.details.list.sum.value,
    label: messages.details.list.sum.label,
  },
  {
    icon: <PaperThreeLinesIcon />,
    value: messages.details.list.repaymentProcedure.value,
    label: messages.details.list.repaymentProcedure.label,
  },
  {
    icon: <WalletOnePocketIcon />,
    value: messages.details.list.deadline.value,
    label: messages.details.list.deadline.label,
  },
  {
    icon: <WheatIcon />,
    value: messages.details.list.crop.value,
    label: messages.details.list.crop.label,
  },
]

export const STEPS: TLandingStep[] = [
  {
    title: messages.steps[1].title,
  },
  {
    title: messages.steps[2].title,
    description: messages.steps[2].description,
  },
  {
    title: messages.steps[3].title,
    description: messages.steps[3].description,
  },
  {
    title: messages.steps[4].title,
  },
  {
    title: messages.steps[5].title,
    description: messages.steps[5].description,
  },
]

export const BENEFITS: TLandingBenefit[] = [
  {
    icon: <ClockIcon />,
    title: messages.benefits.items.decisionMaking,
  },
  {
    icon: <PaperIcon />,
    title: messages.benefits.items.simpleProcedure,
  },
  {
    icon: <StarIcon />,
    title: messages.benefits.items.individualApproach,
  },
  {
    icon: <ShieldDoneIcon />,
    title: messages.benefits.items.reliableLongTermPartnership,
  },
]

export const BREADCRUMB: IBreadcrumb = {
  caption: messages.breadcrumbs.currentPage,
  link: EFinancingOfferRoute.advancePaymentOnFutureHarvest,
}

const LOAN_MAIN_PARAMETERS: TLandingLoanTermGroup = {
  title: messages.loanTerms.termGroups.mainParameters.title,
  columnHeaders: [
    messages.loanTerms.termGroups.mainParameters.columnHeader.byLand,
    messages.loanTerms.termGroups.mainParameters.columnHeader.withBankGuarantee,
    messages.loanTerms.termGroups.mainParameters.columnHeader.standardProcedure,
  ],
  terms: [
    {
      title: messages.loanTerms.termGroups.mainParameters.rows.refusalOfDelivery.title,
      clauses: [
        {
          columns: 3,
          details: [
            { text: messages.loanTerms.termGroups.mainParameters.rows.refusalOfDelivery.clause.common.description },
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.refusalOfDelivery.clause.common.obligations[1],
              checked: true,
            },
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.refusalOfDelivery.clause.common.obligations[2],
              checked: true,
            },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.mainParameters.rows.limit.title,
      clauses: [
        {
          details: [
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.limit.clause.byLand,
            },
          ],
        },
        {
          details: [
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.limit.clause.withBankGuarantee,
            },
          ],
        },
        {
          details: [
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.limit.clause.standardProcedure,
            },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.mainParameters.rows.collateralOptions.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.mainParameters.rows.collateralOptions.clause.byLand }],
        },
        {
          details: [{ text: messages.loanTerms.termGroups.mainParameters.rows.collateralOptions.clause.withBankGuarantee }],
        },
        {
          details: [
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.collateralOptions.clause.standardProcedure,
            },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.mainParameters.rows.interest.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.mainParameters.rows.interest.clause.byLand }],
        },
        {
          details: [{ text: messages.loanTerms.termGroups.mainParameters.rows.interest.clause.withBankGuarantee }],
        },
        {
          details: [
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.interest.clause.standardProcedure,
            },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.mainParameters.rows.additional.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.mainParameters.rows.additional.clause.byLand }],
        },
        {
          details: [{ text: messages.loanTerms.termGroups.mainParameters.rows.additional.clause.withBankGuarantee }],
        },
        {
          details: [
            {
              text: messages.loanTerms.termGroups.mainParameters.rows.additional.clause.standardProcedure,
            },
          ],
        },
      ],
    },
  ],
}

const LOAN_ADDITIONAL_PARAMETERS: TLandingLoanTermGroup = {
  title: messages.loanTerms.termGroups.additionalParameters.title,
  columnHeaders: [],
  terms: [
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.forWhom.title,
      clauses: [
        {
          columns: 1,
          details: [{ text: messages.loanTerms.termGroups.additionalParameters.rows.forWhom.clause.common.description }],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.expenditure.title,
      clauses: [
        {
          details: [
            {
              text: messages.loanTerms.termGroups.additionalParameters.rows.expenditure.clause.common.checks[1],
            },
            {
              text: messages.loanTerms.termGroups.additionalParameters.rows.expenditure.clause.common.checks[2],
            },
            {
              text: messages.loanTerms.termGroups.additionalParameters.rows.expenditure.clause.common.checks[3],
            },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.repaymentSchedule.title,
      clauses: [
        {
          details: [
            { text: messages.loanTerms.termGroups.additionalParameters.rows.repaymentSchedule.clause.common.checks[1] },
            { text: messages.loanTerms.termGroups.additionalParameters.rows.repaymentSchedule.clause.common.checks[2] },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.regionalCoverage.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.additionalParameters.rows.regionalCoverage.clause.common.description }],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.deliveryInterval.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.additionalParameters.rows.deliveryInterval.clause.common.description }],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.maximumTerm.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.additionalParameters.rows.maximumTerm.clause.common.description }],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.pricingOption.title,
      clauses: [
        {
          details: [
            {
              text: messages.loanTerms.termGroups.additionalParameters.rows.pricingOption.clause.common.description,
              formatOptions: {
                LinkToExchange: getLinkFormatOption('https://www.namex.org/ru/commodityauctions/realtimeCA', {
                  className: 'color_green underline underline_green',
                }),
              },
            },
          ],
        },
      ],
    },
    {
      title: messages.loanTerms.termGroups.additionalParameters.rows.advanceAmount.title,
      clauses: [
        {
          details: [{ text: messages.loanTerms.termGroups.additionalParameters.rows.advanceAmount.clause.common.description }],
        },
      ],
    },
  ],
}

export const TERM_GROUPS: TLandingLoanTermGroup[] = [LOAN_MAIN_PARAMETERS, LOAN_ADDITIONAL_PARAMETERS]

export const FAQ: TLandingFAQItem[] = [
  {
    question: messages.faq.questions.requiredDocuments.title,
    answer: messages.faq.questions.requiredDocuments.answer,
  },
  {
    question: messages.faq.questions.propertyCollateral.title,
    answer: messages.faq.questions.propertyCollateral.answer,
  },
  {
    question: messages.faq.questions.advancePaymentStages.title,
    answer: messages.faq.questions.advancePaymentStages.answer,
  },
  {
    question: messages.faq.questions.premiumForHigherProteinLevel.title,
    answer: messages.faq.questions.premiumForHigherProteinLevel.answer,
  },
  {
    question: messages.faq.questions.deliveryToPort.title,
    answer: messages.faq.questions.deliveryToPort.answer,
  },
  {
    question: messages.faq.questions.deliveryLocation.title,
    answer: messages.faq.questions.deliveryLocation.answer,
  },
]

const FAQ_LINK_FORMAT_OPTIONS: Omit<ICommonLinkProps, 'url'> = {
  className: 'color_green underline underline_green',
}

export const FAQ_FORMAT_OPTIONS: Readonly<TFormatOptions> = {
  LinkToQuestionnaire: getLinkFormatOption('https://disk.yandex.ru/i/gJdbpjaAnoV8cw', FAQ_LINK_FORMAT_OPTIONS),
  LinkToLetter: getLinkFormatOption('https://disk.yandex.ru/i/KEM2u-tCYfg5yA', FAQ_LINK_FORMAT_OPTIONS),
  LinkToApplication: getLinkFormatOption('https://disk.yandex.ru/i/Ci2GexeZgOJ5eg', FAQ_LINK_FORMAT_OPTIONS),
  LinkToDocuments: getLinkFormatOption('https://disk.yandex.ru/i/Xky-R6B8SCV86w', FAQ_LINK_FORMAT_OPTIONS),
  LinkToPortList: getLinkFormatOption('https://disk.yandex.ru/i/9bt-R0G_pvvdtw', FAQ_LINK_FORMAT_OPTIONS),
}

export enum ECommonLandingAnchor {
  landingApplyingOfferForm = 'landingApplyingOfferForm',
}

export const LINK_TO_LANDING_VIDEO = 'https://rutube.ru/video/6c9217991cb8aaedb1b8ebe36b7481c0/'
